import React from 'react';
import { ConfigProvider, Select, DatePicker, TimePicker, InputNumber, Switch } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const DATE_FORMAT = 'YYYY-MM-DD';
const DISPLAY_DATE = 'DD MMM YYYY';
const TIME_FORMAT = 'HH:mm';
const DISPLAY_TIME = 'hh:mm A';

export const SLOT_THEME = {
    token: {
        colorPrimary: '#2d207d',
        colorLink: '#2d207d',
        colorText: '#303e67',
        colorTextPlaceholder: '#9aa5c4',
        colorBorder: '#dbe3f0',
        borderRadius: 6,
        controlHeight: 38,
        fontSize: 13,
        fontFamily: '"Roboto", sans-serif',
        zIndexPopupBase: 1100
    },
    components: {
        Select: { optionSelectedBg: '#efedf9', optionActiveBg: '#f6f5fc' },
        DatePicker: { cellActiveWithRangeBg: '#efedf9' },
        Table: {
            headerBg: '#f8f9fc',
            headerColor: '#7081b9',
            headerSplitColor: 'transparent',
            rowHoverBg: '#fbfbfe',
            borderColor: '#e3ebf6',
            cellPaddingBlock: 12,
            cellPaddingInline: 16
        },
        Switch: { colorPrimary: '#2d207d', colorPrimaryHover: '#3b2b9e' }
    }
};

export const SlotThemeProvider = ({ children }) => <ConfigProvider theme={SLOT_THEME}>{children}</ConfigProvider>;

const toDay = (value, format = DATE_FORMAT) => (value ? dayjs(value, format) : null);
const toTimeValue = (value) => (value ? dayjs(value, TIME_FORMAT) : null);

const filterOption = (input, option) => (option?.label || '').toLowerCase().includes(input.toLowerCase());

export const CenterSelect = ({ id, value, onChange, centers, allLabel, placeholder = 'Select center', loading, disabled, error }) => {
    const options = [
        ...(allLabel ? [{ value: '', label: allLabel }] : []),
        ...centers.map(c => ({ value: String(c.value), label: c.label }))
    ];
    return (
        <Select
            id={id}
            showSearch
            value={value === '' && !allLabel ? undefined : value}
            placeholder={loading ? 'Loading centers...' : placeholder}
            options={options}
            onChange={v => onChange(v ?? '')}
            filterOption={filterOption}
            loading={loading}
            disabled={disabled}
            status={error ? 'error' : undefined}
            notFoundContent="No centers found"
            style={{ width: '100%' }}
        />
    );
};

export const SelectField = ({ id, value, onChange, options, disabled, error }) => (
    <Select id={id} value={value} options={options} onChange={onChange} disabled={disabled}
        status={error ? 'error' : undefined} style={{ width: '100%' }} />
);

export const DateField = ({ id, value, onChange, disabled, error, placeholder = 'Select date', allowClear = true, disabledDate }) => (
    <DatePicker
        id={id}
        value={toDay(value)}
        format={DISPLAY_DATE}
        placeholder={placeholder}
        onChange={d => onChange(d ? d.format(DATE_FORMAT) : '')}
        disabled={disabled}
        allowClear={allowClear}
        disabledDate={disabledDate}
        status={error ? 'error' : undefined}
        style={{ width: '100%' }}
    />
);

export const DateRangeField = ({ id, from, to, onChange, disabled, error, allowClear = true, placeholder = ['From date', 'To date'],
    allowEmpty = [false, true], disabledDate }) => (
    <DatePicker.RangePicker
        id={id}
        value={from || to ? [toDay(from), toDay(to)] : null}
        format={DISPLAY_DATE}
        placeholder={placeholder}
        allowEmpty={allowEmpty}
        disabledDate={disabledDate}
        onChange={range => onChange(range?.[0] ? range[0].format(DATE_FORMAT) : '', range?.[1] ? range[1].format(DATE_FORMAT) : '')}
        disabled={disabled}
        allowClear={allowClear}
        status={error ? 'error' : undefined}
        style={{ width: '100%' }}
    />
);

export const MonthField = ({ id, value, onChange }) => (
    <DatePicker
        id={id}
        picker="month"
        value={toDay(value, 'YYYY-MM')}
        format="MMMM YYYY"
        allowClear={false}
        onChange={d => d && onChange(d.format('YYYY-MM'))}
        style={{ width: '100%' }}
    />
);

export const TimeRangeField = ({ id, from, to, onChange, disabled, error, size, placeholder = ['Open', 'Close'] }) => (
    <TimePicker.RangePicker
        id={id}
        value={from || to ? [toTimeValue(from), toTimeValue(to)] : null}
        format={DISPLAY_TIME}
        use12Hours
        minuteStep={5}
        needConfirm={false}
        order={false}
        placeholder={placeholder}
        onChange={range => onChange(range?.[0] ? range[0].format(TIME_FORMAT) : '', range?.[1] ? range[1].format(TIME_FORMAT) : '')}
        disabled={disabled}
        status={error ? 'error' : undefined}
        size={size}
        style={{ width: '100%' }}
    />
);

export const TimeField = ({ id, value, onChange, disabled, error, placeholder = 'Select time' }) => (
    <TimePicker
        id={id}
        value={toTimeValue(value)}
        format={DISPLAY_TIME}
        use12Hours
        minuteStep={5}
        needConfirm={false}
        placeholder={placeholder}
        onChange={t => onChange(t ? t.format(TIME_FORMAT) : '')}
        disabled={disabled}
        status={error ? 'error' : undefined}
        style={{ width: '100%' }}
    />
);

export const NumberField = ({ id, value, onChange, suffix, min, max, placeholder, error, disabled }) => (
    <>
        <InputNumber
            id={id}
            value={value === '' || value === null || value === undefined ? null : Number(value)}
            onChange={v => onChange(v === null || v === undefined ? '' : String(v))}
            min={min}
            max={max}
            precision={0}
            placeholder={placeholder}
            disabled={disabled}
            addonAfter={suffix}
            status={error ? 'error' : undefined}
            style={{ width: '100%' }}
        />
        {error && <span className="ss-field-error">{error}</span>}
    </>
);

export const SwitchField = ({ id, checked, onChange, label, disabled, size = 'default' }) => (
    <label className={`ss-switch ${disabled ? 'is-disabled' : ''}`} htmlFor={id}>
        <Switch id={id} size={size} checked={checked} disabled={disabled} onChange={onChange} />
        {label && <span className="ss-switch-label">{label}</span>}
    </label>
);
